import React, { useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Table, Tag, DatePicker, Select, message } from "antd";
import {
    HeartOutlined,
    RiseOutlined,
    FallOutlined,
    UserOutlined,
    FileTextOutlined,
    BarChartOutlined,
    PieChartOutlined
} from "@ant-design/icons";
import { Badge } from "react-bootstrap";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
} from 'chart.js';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
);

const BloodBankReports = () => {
    const [loading, setLoading] = useState(true);
    const [bloodGroups, setBloodGroups] = useState([]);
    const [donors, setDonors] = useState([]);
    const [requests, setRequests] = useState([]);
    const [dateRange, setDateRange] = useState('all');

    // Stats
    const [stats, setStats] = useState({
        totalDonors: 0,
        activeDonors: 0,
        totalRequests: 0,
        fulfilledRequests: 0,
        pendingRequests: 0,
        totalUnits: 0,
        lowStockGroups: 0
    });

    useEffect(() => {
        loadReportData();
    }, [dateRange]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');

            // Fetch all data
            const [bloodGroupsRes, donorsRes, requestsRes] = await Promise.all([
                oneKlicks.read('', 'blood_groups', { blood_bank_id: userId }, 'group_name ASC'),
                oneKlicks.read('', 'blood_donors', { blood_bank_id: userId }, 'id DESC'),
                oneKlicks.read('', 'blood_requests', { blood_bank_id: userId }, 'id DESC')
            ]);

            const bgData = bloodGroupsRes?.data || [];
            const donorData = donorsRes?.data || [];
            const requestData = requestsRes?.data || [];

            setBloodGroups(bgData);
            setDonors(donorData);
            setRequests(requestData);

            // Calculate stats
            const totalUnits = bgData.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
            const lowStockGroups = bgData.filter(item => (parseInt(item.quantity) || 0) < 5).length;
            const fulfilledRequests = requestData.filter(r => r.status === 'fulfilled').length;
            const pendingRequests = requestData.filter(r => r.status === 'pending').length;
            const activeDonors = donorData.filter(d => d.status === 'active').length;

            setStats({
                totalDonors: donorData.length,
                activeDonors,
                totalRequests: requestData.length,
                fulfilledRequests,
                pendingRequests,
                totalUnits,
                lowStockGroups
            });

        } catch (error) {
            console.error('Error loading report data:', error);
            message.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    // Blood Group Inventory Chart Data
    const bloodGroupChartData = {
        labels: bloodGroups.map(bg => bg.group_name),
        datasets: [
            {
                label: 'Units Available',
                data: bloodGroups.map(bg => parseInt(bg.quantity) || 0),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(255, 159, 64, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(153, 102, 255, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(46, 204, 113, 0.8)',
                    'rgba(52, 73, 94, 0.8)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(255, 159, 64, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(46, 204, 113, 1)',
                    'rgba(52, 73, 94, 1)'
                ],
                borderWidth: 2,
                borderRadius: 8
            }
        ]
    };

    // Request Status Pie Chart
    const requestStatusData = {
        labels: ['Pending', 'Approved', 'Fulfilled', 'Rejected'],
        datasets: [
            {
                data: [
                    requests.filter(r => r.status === 'pending').length,
                    requests.filter(r => r.status === 'approved').length,
                    requests.filter(r => r.status === 'fulfilled').length,
                    requests.filter(r => r.status === 'rejected').length
                ],
                backgroundColor: [
                    'rgba(255, 159, 64, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(46, 204, 113, 0.8)',
                    'rgba(231, 76, 60, 0.8)'
                ],
                borderColor: [
                    'rgba(255, 159, 64, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(46, 204, 113, 1)',
                    'rgba(231, 76, 60, 1)'
                ],
                borderWidth: 2
            }
        ]
    };

    // Donor Status Doughnut Chart
    const donorStatusData = {
        labels: ['Active', 'Inactive'],
        datasets: [
            {
                data: [
                    donors.filter(d => d.status === 'active').length,
                    donors.filter(d => d.status !== 'active').length
                ],
                backgroundColor: [
                    'rgba(46, 204, 113, 0.8)',
                    'rgba(189, 195, 199, 0.8)'
                ],
                borderColor: [
                    'rgba(46, 204, 113, 1)',
                    'rgba(189, 195, 199, 1)'
                ],
                borderWidth: 2
            }
        ]
    };

    // Blood Group Distribution Pie Chart
    const bloodGroupDistribution = {
        labels: bloodGroups.map(bg => bg.group_name),
        datasets: [
            {
                data: bloodGroups.map(bg => parseInt(bg.quantity) || 0),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(255, 159, 64, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(153, 102, 255, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(46, 204, 113, 0.8)',
                    'rgba(52, 73, 94, 0.8)'
                ],
                borderWidth: 2
            }
        ]
    };

    // Urgency Distribution
    const urgencyData = {
        labels: ['Normal', 'Urgent', 'Critical'],
        datasets: [
            {
                label: 'Requests by Urgency',
                data: [
                    requests.filter(r => r.urgency === 'normal' || !r.urgency).length,
                    requests.filter(r => r.urgency === 'urgent').length,
                    requests.filter(r => r.urgency === 'critical').length
                ],
                backgroundColor: [
                    'rgba(52, 152, 219, 0.8)',
                    'rgba(241, 196, 15, 0.8)',
                    'rgba(231, 76, 60, 0.8)'
                ],
                borderRadius: 8
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom'
            }
        }
    };

    const barOptions = {
        ...chartOptions,
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 5
                }
            }
        }
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0 font-weight-bold mr-3">
                                    <BarChartOutlined className="me-2" />
                                    Blood Bank Reports & Analytics
                                </h5>
                                <Badge bg="info">Analytics</Badge>
                            </div>
                            <Select
                                style={{ width: 150 }}
                                value={dateRange}
                                onChange={setDateRange}
                                options={[
                                    { value: 'all', label: 'All Time' },
                                    { value: 'week', label: 'This Week' },
                                    { value: 'month', label: 'This Month' },
                                    { value: 'year', label: 'This Year' }
                                ]}
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <Row gutter={[16, 16]} className="mb-4">
                    <Col xs={24} sm={12} md={6}>
                        <Card className="shadow-sm border-0">
                            <Statistic
                                title="Total Donors"
                                value={stats.totalDonors}
                                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                            <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '8px' }}>
                                <RiseOutlined /> {stats.activeDonors} Active
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="shadow-sm border-0">
                            <Statistic
                                title="Total Requests"
                                value={stats.totalRequests}
                                prefix={<FileTextOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ color: '#faad14' }}
                            />
                            <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '8px' }}>
                                <RiseOutlined /> {stats.fulfilledRequests} Fulfilled
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="shadow-sm border-0">
                            <Statistic
                                title="Total Blood Units"
                                value={stats.totalUnits}
                                prefix={<HeartOutlined style={{ color: '#ff4d4f' }} />}
                                suffix="units"
                                valueStyle={{ color: '#ff4d4f' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="shadow-sm border-0">
                            <Statistic
                                title="Pending Requests"
                                value={stats.pendingRequests}
                                prefix={<FallOutlined style={{ color: '#722ed1' }} />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                            <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '8px' }}>
                                {stats.lowStockGroups} Low Stock Groups
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Charts Row 1 */}
                <Row gutter={[16, 16]} className="mb-4">
                    <Col xs={24} lg={12}>
                        <Card
                            title={<><BarChartOutlined className="me-2" />Blood Inventory by Group</>}
                            className="shadow-sm border-0"
                        >
                            <div style={{ height: '300px' }}>
                                <Bar data={bloodGroupChartData} options={barOptions} />
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card
                            title={<><PieChartOutlined className="me-2" />Blood Group Distribution</>}
                            className="shadow-sm border-0"
                        >
                            <div style={{ height: '300px' }}>
                                <Pie data={bloodGroupDistribution} options={chartOptions} />
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Charts Row 2 */}
                <Row gutter={[16, 16]} className="mb-4">
                    <Col xs={24} md={8}>
                        <Card
                            title={<><PieChartOutlined className="me-2" />Request Status</>}
                            className="shadow-sm border-0"
                        >
                            <div style={{ height: '250px' }}>
                                <Doughnut data={requestStatusData} options={chartOptions} />
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card
                            title={<><PieChartOutlined className="me-2" />Donor Status</>}
                            className="shadow-sm border-0"
                        >
                            <div style={{ height: '250px' }}>
                                <Doughnut data={donorStatusData} options={chartOptions} />
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card
                            title={<><BarChartOutlined className="me-2" />Request Urgency</>}
                            className="shadow-sm border-0"
                        >
                            <div style={{ height: '250px' }}>
                                <Bar data={urgencyData} options={barOptions} />
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Recent Data Tables */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <Card
                            title={<><UserOutlined className="me-2" />Recent Donors</>}
                            className="shadow-sm border-0"
                        >
                            <Table
                                dataSource={donors.slice(0, 5)}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                columns={[
                                    { title: 'Name', dataIndex: 'name', key: 'name' },
                                    {
                                        title: 'Blood',
                                        dataIndex: 'blood_group',
                                        key: 'blood_group',
                                        render: (g) => <Tag color="red">{g}</Tag>
                                    },
                                    {
                                        title: 'Status',
                                        dataIndex: 'status',
                                        key: 'status',
                                        render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s || 'Active'}</Tag>
                                    }
                                ]}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card
                            title={<><FileTextOutlined className="me-2" />Recent Requests</>}
                            className="shadow-sm border-0"
                        >
                            <Table
                                dataSource={requests.slice(0, 5)}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                columns={[
                                    { title: 'Patient', dataIndex: 'patient_name', key: 'patient_name' },
                                    {
                                        title: 'Blood',
                                        dataIndex: 'blood_group',
                                        key: 'blood_group',
                                        render: (g) => <Tag color="red">{g}</Tag>
                                    },
                                    {
                                        title: 'Status',
                                        dataIndex: 'status',
                                        key: 'status',
                                        render: (s) => {
                                            const colors = { pending: 'orange', approved: 'blue', fulfilled: 'green', rejected: 'red' };
                                            return <Tag color={colors[s] || 'default'}>{s || 'Pending'}</Tag>;
                                        }
                                    }
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default BloodBankReports;
